import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Bot, TrendingUp, AlertTriangle, Newspaper, Lightbulb } from 'lucide-react';
import SEO from '../components/SEO';
import AIActivityTimeline from '../components/AIActivityTimeline';
import AIMonitoringStrip from '../components/AIMonitoringStrip';
import AutoDashboardShowcase from '../components/AutoDashboardShowcase';
import { trackIntelClick } from '../components/IntelligenceBar';
import DigestSignup from '../components/DigestSignup';
import { useT } from '../i18n';

/* IntelligencePage — /intelligence. Public showcase of the AI Operating System:
   every curated insight + Google News, sector ticker and the live AI Activity
   timeline in one hub. Destination of the LIVE bar label. */

const API = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  ai: { Icon: Bot, cls: 'text-[#FF7A00] bg-[#FF7A00]/10 border-[#FF7A00]/25', en: 'AI', es: 'IA' },
  revenue: { Icon: TrendingUp, cls: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/25', en: 'Revenue', es: 'Ingresos' },
  alert: { Icon: AlertTriangle, cls: 'text-red-500 bg-red-500/10 border-red-500/25', en: 'Alert', es: 'Alerta' },
  news: { Icon: Newspaper, cls: 'text-sky-500 bg-sky-500/10 border-sky-500/25', en: 'News', es: 'Noticias' },
  insight: { Icon: Lightbulb, cls: 'text-amber-500 bg-amber-500/10 border-amber-500/25', en: 'Insight', es: 'Insight' },
};

const LEVEL_META = {
  breaking: { en: 'BREAKING', es: 'ÚLTIMA HORA', cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
  high: { en: 'HIGH', es: 'ALTA', cls: 'bg-[#FF7A00]/15 text-[#FF9B4A] border-[#FF7A00]/30' },
  tip: { en: 'TIP', es: 'TIP', cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25' },
};

function InsightCard({ item, es, i }) {
  const kind = KIND_META[item.kind] || KIND_META.insight;
  const lvl = LEVEL_META[item.level] || LEVEL_META.tip;
  const external = /^https?:\/\//.test(item.url);
  const title = (es && item.title_es) || item.title;
  const body = (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.35, delay: (i % 3) * 0.05 }}
      className="group h-full flex flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-5 hover:border-[#FF7A00]/50 hover:bg-white/[0.06] transition-all"
    >
      <div className="flex items-center gap-2">
        <span className={`inline-flex w-8 h-8 rounded-lg border items-center justify-center shrink-0 ${kind.cls}`}>
          <kind.Icon className="w-4 h-4" />
        </span>
        <span className={`rounded-[4px] border px-1.5 py-0.5 text-[8.5px] font-bold tracking-[0.1em] leading-none ${lvl.cls}`}>
          {es ? lvl.es : lvl.en}
        </span>
        <span className="ml-auto text-[10px] uppercase tracking-wider font-semibold text-neutral-500">
          {es ? kind.es : kind.en}
        </span>
      </div>
      <p className="mt-3.5 text-[14.5px] font-semibold text-white leading-snug flex-1">{title}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold text-[#FF9B4A] group-hover:text-white transition-colors">
        {item.cta || (es ? 'Ver más' : 'Learn more')}
        {external ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowRight className="w-3.5 h-3.5" />}
      </span>
    </motion.div>
  );
  const common = { className: 'block h-full', onClick: () => trackIntelClick(item.id), 'data-testid': `intel-card-${item.id}` };
  return external
    ? <a href={item.url} target="_blank" rel="noopener noreferrer" {...common}>{body}</a>
    : <Link to={item.url} {...common}>{body}</Link>;
}

export default function IntelligencePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang } = useT();
  const es = lang === 'es';

  const schema = items.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Kitxens Restaurant Intelligence Hub',
        description: es
          ? 'Señales de IA, noticias del sector restaurantero e insights curados por el AI Operating System de Kitxens.'
          : 'AI signals, restaurant sector news and insights curated by the Kitxens AI Operating System.',
        itemListElement: items.slice(0, 20).map((it, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: (es && it.title_es) || it.title,
          url: /^https?:\/\//.test(it.url) ? it.url : `https://kitxens.com${it.url}`,
        })),
      }
    : null;

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetch(`${API}/api/intelligence-feed?limit=40`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (alive && d?.items) setItems(d.items); })
        .catch(() => {})
        .finally(() => alive && setLoading(false));
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <div className="bg-[#0B0D12] text-white overflow-x-hidden" data-testid="intelligence-page">
      <SEO
        path="/intelligence"
        schema={schema}
        title={es ? 'Intelligence Hub | Kitxens — Inteligencia del sector en vivo' : 'Intelligence Hub | Kitxens — Live restaurant sector intelligence'}
        description={es
          ? 'El sector restaurantero, decodificado en tiempo real: señales de IA, noticias, alertas y la actividad del AI Operating System de Kitxens.'
          : 'The restaurant sector, decoded in real time: AI signals, news, alerts and the live activity of the Kitxens AI Operating System.'}
      />

      {/* Hero */}
      <section className="relative pt-28 pb-12 lg:pt-32 lg:pb-16 overflow-hidden">
        <div className="pointer-events-none absolute -top-32 right-0 w-[520px] h-[520px] rounded-full bg-[#FF7A00]/[0.10] blur-3xl" />
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10 relative">
          <p className="inline-flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase font-bold text-emerald-400">
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
              <span className="relative w-2 h-2 rounded-full bg-emerald-400" />
            </span>
            {es ? 'En vivo · Intelligence Hub' : 'Live · Intelligence Hub'}
          </p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] leading-[1.05] max-w-3xl">
            {es ? 'La inteligencia que montamos para tu restaurante.' : 'The intelligence we build for your restaurant.'}
          </h1>
          <p className="mt-5 text-neutral-300 text-lg leading-relaxed max-w-2xl">
            {es
              ? 'Esto es un ejemplo en vivo de lo que nuestros equipos humanos + IA saben hacer: dashboards, señales del sector, noticias y un sistema trabajando de forma autónoma — el mismo que montaríamos para ti.'
              : 'This is a live example of what our human + AI teams can do: dashboards, sector signals, news and a system working autonomously — the same one we would build for you.'}
          </p>
        </div>
      </section>

      <AutoDashboardShowcase />

      {/* Insights grid */}
      <section className="py-14 lg:py-20" data-testid="intelligence-insights">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between gap-4 mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-[-0.015em]">
              {es ? 'Todos los insights, editados por la IA.' : 'Every insight, edited by the AI.'}
            </h2>
            <p className="text-[12px] text-neutral-500 shrink-0 mb-1">
              {es ? 'Curado por Emergent · Score editorial autónomo' : 'Curated by Emergent · Autonomous editorial score'}
            </p>
          </div>
          {loading && <p className="text-neutral-500 py-10">{es ? 'Cargando…' : 'Loading…'}</p>}
          {!loading && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item, i) => <InsightCard key={item.id} item={item} es={es} i={i} />)}
            </div>
          )}
        </div>
      </section>

      <AIActivityTimeline />

      <DigestSignup />

      {/* CTA */}
      <section className="pt-10 pb-20 lg:pb-24">
        <div className="max-w-[880px] mx-auto px-6 lg:px-10">
          <div className="rounded-3xl bg-gradient-to-br from-[#FF7A00] to-[#ff9b4a] text-white p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold tracking-[-0.015em] leading-tight max-w-md">
                {es ? '¿Quieres esta inteligencia trabajando para tu restaurante?' : 'Want this intelligence working for your restaurant?'}
              </h3>
            </div>
            <div className="flex flex-wrap gap-3 shrink-0">
              <Link to="/contact?type=discovery" className="inline-flex items-center gap-1.5 bg-white text-[#F77F11] hover:bg-neutral-100 h-12 px-6 rounded-full text-sm font-semibold transition-colors" data-testid="intelligence-cta-discovery">
                {es ? 'Agendar Discovery Call' : 'Book a Discovery Call'} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/os" className="inline-flex items-center gap-1.5 border border-white/40 hover:bg-white/10 text-white h-12 px-6 rounded-full text-sm font-semibold transition-colors" data-testid="intelligence-cta-os">
                {es ? 'Ver el OS en vivo' : 'Watch the OS live'}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
